import Charts from 'components/Charts';
import React, { useState, useEffect } from 'react';
import api from 'api';
import Router from 'components/Router/Router';
import { formatPercent, Notify } from 'utils';
import { Button, Modal } from 'components/Base';
import BenchmarkManager from 'components/Modals/BenchmarkManager/BenchmarkManager';
import { compose } from 'ramda';
import Store from 'store/store';
import { MetaType } from 'store/modules/Meta/types';

const { connect, selectors } = Store;
const { useParams } = Router;

interface PastPerformanceProps {
  data: Array<any>;
  earliestDates: any;
  footprints?: any;
  height?: number;
  isAnyPolicyAdded?: boolean;
  errors?: string[];
  limits?: string[];
  hasDateControl?: boolean;
  benchmarks: MetaType[];
  isAnimationActive?: boolean;
  bigLabels?: boolean;
  isForPdf?: boolean;
  getPastPerformanceData?: Function;
  setData?: Function;
  canManageBenchmarks?: boolean;
  id?: number | null;
  benchmarkConfig: number[];
  provider?: any;
  range?: any;
  localStorageKey?: string;
}

const PastPerformance = ({
  data,
  earliestDates,
  footprints,
  height = 700,
  isAnyPolicyAdded = true,
  errors,
  limits,
  hasDateControl = true,
  benchmarks,
  isAnimationActive = true,
  bigLabels = false,
  isForPdf = false,
  canManageBenchmarks = true,
  // eslint-disable-next-line @typescript-eslint/no-empty-function
  getPastPerformanceData = () => {},
  // eslint-disable-next-line @typescript-eslint/no-empty-function
  setData = () => {},
  id = null,
  benchmarkConfig = [],
  provider,
  range,
  localStorageKey = ''
}: PastPerformanceProps) => {
  const [pastPerformanceData, setPastPerformanceData] = useState(data || []);
  const [mappedData, setMappedData] = useState(data || []);
  const [fixedBenchmarks, setFixedBenchmarks] = useState([] as any);
  const [firmBenchmarks, setFirmBenchmarks] = useState([] as any);
  const [allMorningstarBenchmarks, setAllMorningstarBenchmarks] = useState(
    [] as any
  );
  const [morningstarBenchmarkConfig, setMorningstarBenchmarkConfig] = useState(
    [] as any
  );
  const [excludedMorningstarBenchmarkIds, setExcludedMorningstarBenchmarkIds] =
    useState([] as any);
  const [benchmarkList, setBenchmarkList] = useState([] as any);
  const [isBenchmarkModalOpen, setIsBenchmarkModalOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [dates, setDates] = useState(earliestDates || {});
  const [currentFetchData, setCurrentFetchData] = useState({
    dates: [],
    excludedPolicies: [] as any
  });
  const { portfolio } = useParams() as any;

  useEffect(() => {
    const promise = benchmarkConfig.length
      ? new Promise(resolve => {
          resolve({ data: benchmarkConfig });
        })
      : api.getBenchmarkConfig();

    promise.then(({ data }) => {
      setFirmBenchmarks(data);
      if (!canManageBenchmarks) {
        setIsLoading(false);
        return;
      }

      Promise.all([
        id
          ? api.getAnalysisFixedBenchmarks(id)
          : api.getFixedBenchmarks(portfolio),
        api.getMorningstarBenchmarks(),
        api.getMorningstarBenchmarkConfig()
      ]).then(
        ([
          fixedBenchmarksResponse,
          morningstarBenchmarksResponse,
          morningstarBenchmarkConfigResponse
        ]) => {
          setFixedBenchmarks(fixedBenchmarksResponse.data.results);
          setAllMorningstarBenchmarks(morningstarBenchmarksResponse.data);
          setMorningstarBenchmarkConfig(
            morningstarBenchmarkConfigResponse.data
          );
          setIsLoading(false);
        }
      );
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    setExcludedMorningstarBenchmarkIds(
      morningstarBenchmarkConfig.map((result: any) => result.external_id)
    );
  }, [morningstarBenchmarkConfig]);

  useEffect(() => {
    setBenchmarkList([
      ...benchmarks
        .map((benchmark: any) => {
          return {
            ...benchmark,
            type: benchmark.name.split(' ')[0],
            selected: !!fixedBenchmarks.find(
              (fixedBenchmark: any) =>
                fixedBenchmark.benchmark_id == benchmark.value
            )?.active,
            benchmarkType: 'firm'
          };
        })
        .filter((benchmark: any) =>
          firmBenchmarks.includes(parseInt(benchmark.value))
        ),
      ...Object.values(allMorningstarBenchmarks)
        .flatMap((group: any) => {
          return group.benchmarks.map((benchmark: any) => {
            return {
              name: benchmark.name,
              value: benchmark.secId,
              type: group.name,
              selected: !!fixedBenchmarks.find(
                (fixedBenchmark: any) =>
                  fixedBenchmark.benchmark_id == benchmark.secId
              )?.active,
              benchmarkType: 'morningstar'
            };
          });
        })
        .filter(
          (benchmark: any) =>
            !excludedMorningstarBenchmarkIds.includes(benchmark.value)
        )
    ]);
  }, [
    excludedMorningstarBenchmarkIds,
    allMorningstarBenchmarks,
    firmBenchmarks,
    fixedBenchmarks
  ]);

  const compoundInterest = (t: number, r: number) => {
    const amount = Math.pow(1 + r, t);
    const interest = amount - 1;
    return interest;
  };

  useEffect(() => {
    const dataCopy = JSON.parse(JSON.stringify(pastPerformanceData));
    setMappedData(
      dataCopy.map((item: any, index: number) => {
        fixedBenchmarks
          .filter(
            (fixedBenchmark: any) =>
              fixedBenchmark.active && !fixedBenchmark.benchmark_id
          )
          .forEach((fixedBenchmark: any) => {
            const annualIncrease = Number(fixedBenchmark.annual_increase);
            const key = `Fixed benchmark - ${formatPercent(
              annualIncrease * 100
            )} PA`;

            item[key] = Math.max(
              0,
              compoundInterest(index / 365, annualIncrease)
            );
          });

        fixedBenchmarks
          .filter(
            (fixedBenchmark: any) =>
              !fixedBenchmark.active && fixedBenchmark.benchmark_id
          )
          .forEach((fixedBenchmark: any) => {
            const match = benchmarks.find(
              benchmark =>
                Number(benchmark.value) === Number(fixedBenchmark.benchmark_id)
            );
            const name = match && match.name;
            if (name) {
              delete item[name];
            }
          });

        return item;
      })
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pastPerformanceData, fixedBenchmarks]);

  const fetchData = (dates: any, excludedPolicies: string[]) => {
    setCurrentFetchData({ dates, excludedPolicies });
    setIsLoading(true);

    const identifier = {} as any;
    if (id) {
      identifier.id = id;
    } else if (portfolio) {
      identifier.portfolio = portfolio;
    } else {
      identifier.provider = provider.id;
      identifier.range = range;
    }

    const postData = {
      excluded_policies: excludedPolicies
    } as any;

    if (dates.length) {
      postData.date_data = { date: dates[0] };
    }

    getPastPerformanceData({
      ...identifier,
      data: postData
    })
      .then(({ data }: any) => {
        setData(data);
        setPastPerformanceData(data.data);
        setDates(data.earliest_dates);
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  const saveBenchmarks = (benchmarks: any) => {
    let method = null;
    if (id) {
      method = api.updateAnalysisFixedBenchmarks(id, benchmarks);
    } else if (portfolio) {
      method = api.updateCaseBenchmarks(portfolio, benchmarks);
    }

    if (!method) return;

    method
      .then(({ data }) => {
        Notify.success('Benchmarks updated successfully.');
        setFixedBenchmarks(data);
        fetchData(currentFetchData.dates, currentFetchData.excludedPolicies);
      })
      .catch(() => {
        Notify.error('Benchmarks could not be updated.');
      })
      .finally(() => {
        setIsBenchmarkModalOpen(false);
      });
  };

  const fixedBenchmarkButton = (
    <Button color="primary" onClick={() => setIsBenchmarkModalOpen(true)}>
      Manage Benchmarks
    </Button>
  );

  return (
    <>
      <Charts.IPS.PastPerformance
        data={mappedData}
        earliestDates={dates}
        footprints={footprints}
        height={height}
        isForPdf={isForPdf}
        isAnyPolicyAdded={isAnyPolicyAdded}
        errors={errors}
        limits={limits}
        fixedBenchmarkButton={
          canManageBenchmarks ? fixedBenchmarkButton : <></>
        }
        hasDateControl={hasDateControl}
        isAnimationActive={isAnimationActive}
        bigLabels={bigLabels}
        getPastPerformanceData={(dates: any, excludedPolicies: string[]) =>
          fetchData(dates, excludedPolicies)
        }
        isLoading={isLoading}
        setData={setData}
        localStorageKey={localStorageKey}
      />
      {canManageBenchmarks && (
        <Modal
          isOpen={isBenchmarkModalOpen}
          closeModalFunc={() => setIsBenchmarkModalOpen(false)}
        >
          <BenchmarkManager
            closeModal={() => setIsBenchmarkModalOpen(false)}
            fixedBenchmarks={fixedBenchmarks}
            benchmarkList={benchmarkList}
            saveBenchmarks={saveBenchmarks}
            identifier={id || portfolio}
          />
        </Modal>
      )}
    </>
  );
};

export default compose(
  connect((state: any) => ({
    benchmarks: selectors.metaSelectors.benchmarks(state),
    benchmarkConfig: selectors.metaSelectors.benchmarkConfig(state)
  }))
)(PastPerformance);
